// src/components/StaffLayout.js
import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FiUser, FiUsers, FiCalendar, FiLogOut } from "react-icons/fi";

export default function StaffLayout({ children }) {
  const { user, logout } = useAuth();

  const iconLinkClass = (variant) => ({ isActive }) =>
    `adm-ic adm-ic--${variant} ${isActive ? "active" : ""}`;

  const displayName = user?.name || user?.username || "Staff";
  const initials = String(displayName || "S")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase() || "")
    .join("");

  return (
    <div className="adm-shell">
      <style>{`
        :root{
          --adm-bg:#f3f6fb;
          --adm-card:#ffffff;
          --adm-text:#0f172a;
          --adm-muted:#64748b;
          --adm-line: rgba(2, 6, 23, .10);
          --adm-shadow: 0 10px 22px rgba(2, 6, 23, .06);
          --adm-blue:#2563eb;
          --adm-side-w: 86px;
        }

        .adm-shell{
          min-height: 100dvh;
          background:
            radial-gradient(circle at top left, rgba(59,130,246,.05), transparent 28%),
            linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%);
          overflow-x: hidden;
        }

        .adm-side{
          position: fixed;
          top: 0;
          left: 0;
          width: var(--adm-side-w);
          height: 100dvh;
          background: rgba(255,255,255,.92);
          backdrop-filter: blur(14px);
          border-right: 1px solid rgba(2, 6, 23, .08);
          display:flex;
          flex-direction:column;
          align-items:center;
          padding: 14px 10px 12px;
          gap: 12px;
          z-index: 40;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(2,6,23,.05);
        }

        .adm-side::before{
          content:"";
          position:absolute;
          top:0;
          left:0;
          right:0;
          height:120px;
          background: linear-gradient(180deg, rgba(37,99,235,.06), rgba(37,99,235,0));
          pointer-events:none;
        }

        .adm-top{
          width: 100%;
          display:flex;
          flex-direction:column;
          align-items:center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }

        .adm-avatar-mini{
          width: 58px;
          height: 58px;
          border-radius: 18px;
          background: linear-gradient(135deg, #eef2ff 0%, #dbeafe 100%);
          border: 1px solid rgba(37,99,235,.14);
          box-shadow:
            0 10px 20px rgba(37,99,235,.10),
            inset 0 1px 0 rgba(255,255,255,.75);
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight: 950;
          font-size: 16px;
          color: #1d4ed8;
          letter-spacing: -.3px;
        }

        .adm-presence-dot{
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: #22c55e;
          box-shadow: 0 0 0 3px rgba(34,197,94,.18);
        }

        .adm-nav{
          width: 100%;
          display:flex;
          flex-direction:column;
          align-items:center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }

        .adm-ic{
          width: 56px;
          height: 54px;
          border-radius: 18px;
          display:flex;
          align-items:center;
          justify-content:center;
          text-decoration:none;
          color: var(--adm-text);
          border: 1px solid transparent;
          font-size: 18px;
          font-weight: 900;
          transition:
            transform .16s ease,
            background .16s ease,
            box-shadow .16s ease,
            border-color .16s ease;
          cursor: pointer;
          background: transparent;
          position: relative;
        }

        .adm-ic::after{
          content:"";
          position:absolute;
          left: 50%;
          bottom: 5px;
          width: 18px;
          height: 3px;
          border-radius: 999px;
          transform: translateX(-50%) scaleX(0);
          transition: transform .16s ease, background .16s ease;
          background: transparent;
        }

        .adm-ic:hover{
          background: rgba(15,23,42,.04);
          transform: translateY(-1px);
          box-shadow: 0 10px 18px rgba(2,6,23,.06);
        }

        .adm-ic.active{
          box-shadow:
            0 12px 20px rgba(2,6,23,.08),
            inset 0 1px 0 rgba(255,255,255,.75);
        }

        .adm-ic.active::after{
          transform: translateX(-50%) scaleX(1);
        }

        .adm-ic--my.active{
          background: linear-gradient(135deg, rgba(37,99,235,.14), rgba(147,197,253,.14));
          border-color: rgba(37,99,235,.20);
        }
        .adm-ic--my.active::after{
          background: #2563eb;
        }

        .adm-ic--team.active{
          background: linear-gradient(135deg, rgba(124,58,237,.14), rgba(196,181,253,.14));
          border-color: rgba(124,58,237,.20);
        }
        .adm-ic--team.active::after{
          background: #7c3aed;
        }

        .adm-ic--calendar.active{
          background: linear-gradient(135deg, rgba(245,158,11,.14), rgba(253,224,71,.16));
          border-color: rgba(245,158,11,.22);
        }
        .adm-ic--calendar.active::after{
          background: #f59e0b;
        }

        .adm-ico{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          line-height: 1;
        }

        .ico-my{ color: #2563eb; }
        .ico-team{ color: #7c3aed; }
        .ico-cal{ color: #f59e0b; }
        .ico-logout{ color: #ef4444; }

        .adm-spacer{ flex:1; }

        .adm-bottom{
          width: 100%;
          display:flex;
          flex-direction:column;
          align-items:center;
          gap: 12px;
          padding-top: 10px;
          border-top: 1px solid rgba(2,6,23,.06);
          position: relative;
          z-index: 1;
        }

        .adm-bottom .adm-ic{
          border: 1px solid rgba(2,6,23,.08);
          background: rgba(255,255,255,.7);
        }

        .adm-bottom .adm-ic:hover{
          background: linear-gradient(135deg, rgba(239,68,68,.08), rgba(254,202,202,.10));
          border-color: rgba(239,68,68,.14);
        }

        .adm-logo{
          width: 56px;
          height: 56px;
          border-radius: 18px;
          overflow:hidden;
          border: 1px solid rgba(2,6,23,.08);
          box-shadow:
            0 10px 18px rgba(2,6,23,.06),
            inset 0 1px 0 rgba(255,255,255,.65);
          background:#fff;
          display:flex;
          align-items:center;
          justify-content:center;
        }

        .adm-logo img{
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transform: scale(4.25);
          transform-origin: 50% 24%;
        }

        .adm-main{
          min-width: 0;
          padding: 18px 22px;
          padding-left: calc(var(--adm-side-w) + 22px);
        }

        @media (max-width: 900px){
          .adm-side{
            position: fixed;
            top: auto;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            height: 78px;
            flex-direction: row;
            justify-content: space-around;
            border-right: none;
            border-top: 1px solid rgba(2,6,23,.08);
            padding: 10px 12px;
            gap: 8px;
            background: rgba(255,255,255,.94);
            backdrop-filter: blur(16px);
          }

          .adm-side::before{
            display:none;
          }

          .adm-top,
          .adm-spacer,
          .adm-logo{
            display:none;
          }

          .adm-nav{
            flex-direction: row;
            justify-content: space-around;
            width: auto;
            gap: 8px;
            flex: 1;
          }

          .adm-bottom{
            width: auto;
            border-top: none;
            padding-top: 0;
            gap: 0;
          }

          .adm-ic{
            width: 52px;
            height: 50px;
            border-radius: 16px;
          }

          .adm-main{
            padding: 14px;
            padding-bottom: 96px;
          }
        }
      `}</style>

      <aside className="adm-side" aria-label={`Staff navigation for ${displayName}`}>
        <div className="adm-top">
          <div className="adm-avatar-mini" title={displayName}>
            {initials || "ST"}
          </div>
          <div className="adm-presence-dot" aria-hidden="true" />
        </div>

        <div className="adm-nav">
          <NavLink
            to="/my-schedule"
            className={iconLinkClass("my")}
            title="My Schedule"
            aria-label="My Schedule"
          >
            <span className="adm-ico ico-my">
              <FiUser size={19} />
            </span>
          </NavLink>

          <NavLink
            to="/user/team-schedule"
            className={iconLinkClass("team")}
            title="Team Schedule"
            aria-label="Team Schedule"
          >
            <span className="adm-ico ico-team">
              <FiUsers size={19} />
            </span>
          </NavLink>

          <NavLink
            to="/user/calendar"
            className={iconLinkClass("calendar")}
            title="Calendar"
            aria-label="Calendar"
          >
            <span className="adm-ico ico-cal">
              <FiCalendar size={19} />
            </span>
          </NavLink>
        </div>

        <div className="adm-spacer" />

        <div className="adm-bottom">
          <button
            type="button"
            className="adm-ic"
            onClick={logout}
            title="Logout"
            aria-label="Logout"
          >
            <span className="adm-ico ico-logout">
              <FiLogOut size={19} />
            </span>
          </button>

          <div className="adm-logo" title="Logo">
            <img src="/logo.png" alt="Logo" />
          </div>
        </div>
      </aside>

      <main className="adm-main">{children}</main>
    </div>
  );
}